import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Button,
  CircularProgress,
} from '@mui/material';
import api from '../../services/api';

const LeadSourcePerformanceReportPage = ({ dateRangeState, selectedTeams = [] }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (!dateRangeState?.currentStart || !dateRangeState?.currentEnd) return;

    const fetchData = async () => {
      try {
        setLoading(true);
        const startDate = dateRangeState.currentStart.format('YYYY-MM-DD');
        const endDate = dateRangeState.currentEnd.format('YYYY-MM-DD');

        const requestData = {
          period_type: 'custom',
          date_range: {
            start_date: startDate,
            end_date: endDate,
          },
        };

        if (selectedTeams && selectedTeams.length > 0) {
          requestData.team_filter = selectedTeams;
        }

        const response = await api.post('/report/lead/source-performance/', requestData);
        const data = response.data || {};

        // Map dashboard payload to table rows
        const sources = data.sources || [];
        const mappedRows = sources.map((src) => ({
          leadSource: src.name,
          totalLeads: src.leads,
          untouchedLeads: src.untouchedLeads ?? 0,
          connectedLeads: src.connectedLeads ?? 0,
          qualifiedLeads: src.qualified ?? 0,
          visitsCompleted: src.visitsCompleted ?? 0,
          leadsConverted: src.conversion ?? 0,
          notInterestedLeads: src.notInterestedLeads ?? 0,
        }));

        setRows(mappedRows);
      } catch (error) {
        console.error('Error fetching lead source performance:', error);
        setRows([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [dateRangeState, selectedTeams]);

  const handleExportCSV = () => {
    try {
      if (!rows || rows.length === 0) {
        alert('No data to export');
        return;
      }

      setExporting(true);

      // Define CSV headers
      const headers = [
        'Lead Source',
        'Total Leads',
        'Untouched Leads',
        'Connected Leads',
        'Qualified Leads',
        'Visits Completed',
        'Leads Converted',
        'Not Interested Leads',
      ];

      // Escape CSV values properly
      const escapeCSV = (value) => {
        if (value === null || value === undefined) return '';
        const stringValue = String(value);
        // If contains comma, newline, or quote, wrap in quotes and escape quotes
        if (stringValue.includes(',') || stringValue.includes('\n') || stringValue.includes('"')) {
          return `"${stringValue.replace(/"/g, '""')}"`;
        }
        return stringValue;
      };

      // Convert rows to CSV format
      const csvRows = [
        headers.join(','),
        ...rows.map((row) =>
          [
            escapeCSV(row.leadSource),
            escapeCSV(row.totalLeads),
            escapeCSV(row.untouchedLeads),
            escapeCSV(row.connectedLeads),
            escapeCSV(row.qualifiedLeads),
            escapeCSV(row.visitsCompleted),
            escapeCSV(row.leadsConverted),
            escapeCSV(row.notInterestedLeads),
          ].join(',')
        ),
      ];

      const csvContent = csvRows.join('\n');

      // Add BOM for Excel compatibility
      const BOM = '\uFEFF';
      const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      // Generate filename with date range if available
      let filename = 'lead_source_performance';
      if (dateRangeState?.currentStart && dateRangeState?.currentEnd) {
        const startDate = dateRangeState.currentStart.format('YYYY-MM-DD');
        const endDate = dateRangeState.currentEnd.format('YYYY-MM-DD');
        filename = `lead_source_performance_${startDate}_to_${endDate}`;
      } else {
        filename = `lead_source_performance_${new Date().toISOString().split('T')[0]}`;
      }
      
      link.setAttribute('href', url);
      link.setAttribute('download', `${filename}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();

      // Clean up
      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        setExporting(false);
      }, 100);
    } catch (error) {
      console.error('Error exporting CSV:', error);
      alert('Failed to export CSV. Please try again.');
      setExporting(false);
    }
  };

  return (
    <Box sx={{ flexGrow: 1, pt: 0, pb: 3 }}>
      <Box
        sx={{
          mb: 2,
          px: { xs: 2, sm: 3 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box>
          <Typography
            variant="h6"
            sx={{ fontWeight: 'bold', color: 'text.primary', mb: 0.5 }}
          >
            Lead Source Performance Report
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 520 }}>
            Performance of each lead source across the funnel.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          size="small"
          sx={{
            textTransform: 'none',
            borderRadius: 2,
            fontWeight: 500,
          }}
          onClick={handleExportCSV}
          disabled={exporting || rows.length === 0}
        >
          {exporting ? 'Exporting...' : 'Export CSV'}
        </Button>
      </Box>
      <Box sx={{ px: { xs: 2, sm: 3 } }}>
        <Paper
          elevation={2}
          sx={{
            borderRadius: 2,
            overflow: 'hidden',
            boxShadow: (theme) => theme.shadows[2],
            transition: 'all 0.3s ease-in-out',
          }}
        >
          <TableContainer
            sx={{
              overflowX: 'auto',
              '&::-webkit-scrollbar': {
                height: 8,
              },
              '&::-webkit-scrollbar-thumb': {
                borderRadius: 4,
                backgroundColor: 'rgba(0,0,0,0.2)',
              },
            }}
          >
            <Table size="medium" stickyHeader>
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor: (theme) => theme.palette.grey[50],
                  }}
                >
                  <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Lead Source</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Total Leads</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Untouched Leads</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Connected Leads</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Qualified Leads</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Visits Completed</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Leads Converted</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Not Interested Leads</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={40} />
                    </TableCell>
                  </TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        No data available for the selected period
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row, idx) => (
                    <TableRow
                      key={idx}
                      hover
                      sx={(theme) => ({
                        height: theme.spacing(7),
                        transition: 'background-color 0.2s ease',
                        '&:hover': {
                          backgroundColor: theme.palette.action.hover,
                        },
                        '&:last-child td, &:last-child th': { border: 0 },
                      })}
                    >
                      <TableCell>{row.leadSource}</TableCell>
                      <TableCell align="right">{row.totalLeads}</TableCell>
                      <TableCell align="right">{row.untouchedLeads}</TableCell>
                      <TableCell align="right">{row.connectedLeads}</TableCell>
                      <TableCell align="right">{row.qualifiedLeads}</TableCell>
                      <TableCell align="right">{row.visitsCompleted}</TableCell>
                      <TableCell align="right">{row.leadsConverted}</TableCell>
                      <TableCell align="right">{row.notInterestedLeads}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
    </Box>
  );
};

export default LeadSourcePerformanceReportPage;


